import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AlertErrorMessage } from '../../../../shared/alert/alert-error'
import { AuthNotice } from '../../../../shared/sso-workspace/auth-card'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { SCHEDULED_REASON, SCHEDULED_SIGN_IN_MESSAGE } from '../../../../shared/organization-deletion/organization-deletion'

// The auth service reports a login lockout as "...failed attempts in HH:MM:SS",
// where HH:MM:SS is the time REMAINING until unlock. Detect that, lock onto the
// resulting unlock instant, and render a live animated countdown; delegate any
// other error to the normal alert.
const LOCKOUT_RE = /failed attempts in\s+(\d{1,2}):(\d{2}):(\d{2})/i

const parseRemainingSeconds = error => {
  const msg = error && error.message
  if (!msg) return null
  const m = String(msg).match(LOCKOUT_RE)
  if (!m) return null
  return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3])
}

export const LoginErrorAlert = ({ error }) => {
  // Persist the active lockout across re-renders. The server's unlock time is
  // stable while locked, so repeated attempts during ONE lockout must keep the
  // same target + total — otherwise the bar restarts at 100% every attempt and
  // it looks like the full window resets each time.
  const lockoutRef = useRef(null)

  const lockout = useMemo(() => {
    const remaining = parseRemainingSeconds(error)
    if (remaining == null) return null

    const unlockAt = Date.now() + remaining * 1000
    const prev = lockoutRef.current

    // Same lockout: the newly-computed unlock is at (or before) the one we're
    // already tracking, within a small tolerance for request/clock jitter. Keep
    // the original unlock + total so the countdown continues rather than resets.
    if (prev && unlockAt <= prev.unlockAt + 2000) {
      lockoutRef.current = { unlockAt: prev.unlockAt, totalSeconds: Math.max(prev.totalSeconds, remaining) }
    } else {
      lockoutRef.current = { unlockAt, totalSeconds: remaining }
    }

    return lockoutRef.current
  }, [error])

  // A member or admin of an organization scheduled for deletion (auth's
  // reason organization_scheduled_for_deletion): our words, not the raw
  // message.
  if (error && error.reason === SCHEDULED_REASON) {
    return (
      <AuthNotice tone="warn" role="alert">
        <p className="m-0">{SCHEDULED_SIGN_IN_MESSAGE}</p>
        <ErrorReference error={error} />
      </AuthNotice>
    )
  }

  if (!lockout) {
    return <AlertErrorMessage error={error} />
  }

  return <LockoutCountdown unlockAt={lockout.unlockAt} totalSeconds={lockout.totalSeconds} />
}

const format = totalSeconds => {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const LockoutCountdown = ({ unlockAt, totalSeconds }) => {
  // Tick a clock and derive everything from the fixed unlockAt, so the timer and
  // bar are drift-free and stay consistent no matter how often the alert re-renders.
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [])

  const remainingExact = Math.max(0, (unlockAt - now) / 1000)
  const remaining = Math.ceil(remainingExact)

  if (remaining <= 0) {
    return (
      <div className="alert alert-success">
        You can try signing in again now.
      </div>
    )
  }

  const pct = Math.max(0, Math.min(100, (remainingExact / totalSeconds) * 100))

  return (
    <div className="alert alert-warning" role="status" aria-live="polite">
      <div className="d-flex justify-content-between align-items-center">
        <span>Too many attempts — try again in</span>
        <span className="fw-bold fs-5 font-monospace">{format(remaining)}</span>
      </div>
      <div className="progress mt-2" style={{ height: '4px' }}>
        <div
          className="progress-bar bg-warning"
          role="progressbar"
          aria-valuenow={remaining}
          aria-valuemin="0"
          aria-valuemax={totalSeconds}
          style={{ width: `${pct}%`, transition: 'width 0.25s linear' }}
        />
      </div>
    </div>
  )
}
